import { Select } from "@mantine/core";
import * as React from "react";
import { getZoomSafePortalTarget } from "../utils/zoomSafePortal";
import classes from "./FieldSelect.module.css";

export interface FieldSelectOption {
  value: string;
  /** Chữ hiện trong ô sau khi chọn, cũng là chữ dùng để lọc khi gõ */
  label: string;
  /** Phần đậm của dòng trong danh sách (ví dụ mã vị trí); không có thì dùng label */
  primary?: string;
  /** Phần nhạt sau phần đậm (ví dụ tên vị trí) */
  hint?: string;
}

interface FieldSelectProps {
  id?: string;
  /** Nhãn nhỏ nằm trong ô bên trái (thanh quét) */
  caption?: string;
  /** Bề rộng dành cho nhãn trong ô */
  captionWidth?: number;
  required?: boolean;
  ariaLabel?: string;
  value: string;
  placeholder: string;
  options: FieldSelectOption[];
  disabled?: boolean;
  /** Bắt buộc mà chưa chọn: viền đứt màu cam */
  needed?: boolean;
  /** Bề rộng cả ô; không có thì giãn hết khung */
  width?: number;
  height?: number;
  /** Bề rộng danh sách xổ xuống; không có thì bằng ô */
  dropdownWidth?: number;
  /** Gõ để lọc danh sách */
  searchable?: boolean;
  /** Ô trong lưới: thấp, chữ nhỏ */
  compact?: boolean;
  /** Không viền, chỉ hiện khung khi rê chuột (ô sửa trong lưới) */
  plain?: boolean;
  title?: string;
  onChange: (value: string) => void;
}

const CheckIcon = () => (
  <svg
    className={classes.check}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

/**
 * Ô chọn của trang nhập đổi trả: Mantine Select mặc kiểu thiết kế (viền xám, viền đứt cam khi còn thiếu,
 * nền xám khi khoá; danh sách bo 12, dòng đang chọn tô xanh nhạt kèm dấu tích, mã đậm + tên nhạt).
 */
const FieldSelect = ({
  id,
  caption,
  captionWidth = 64,
  required,
  ariaLabel,
  value,
  placeholder,
  options,
  disabled = false,
  needed = false,
  width,
  height = 38,
  dropdownWidth,
  searchable = false,
  compact = false,
  plain = false,
  title,
  onChange,
}: FieldSelectProps) => {
  const byValue = React.useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
  const data = React.useMemo(() => options.map((option) => ({ value: option.value, label: option.label })), [options]);
  const inputClass = [classes.input, compact ? classes.compact : "", plain ? classes.plain : ""].filter(Boolean).join(" ");

  return (
    <Select
      id={id}
      aria-label={ariaLabel ?? caption}
      title={title}
      data={data}
      value={value || null}
      onChange={(next) => onChange(next ?? "")}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
      allowDeselect={false}
      nothingFoundMessage="Không có kết quả phù hợp"
      maxDropdownHeight={300}
      w={width ?? "100%"}
      style={{ flexShrink: width ? 0 : 1, maxWidth: "100%" }}
      data-needed={needed || undefined}
      leftSection={
        caption ? (
          <span className={`${classes.caption} ${needed ? classes.captionNeeded : ""}`}>
            {caption}
            {required && <b>*</b>}
          </span>
        ) : undefined
      }
      leftSectionWidth={caption ? captionWidth : undefined}
      leftSectionPointerEvents="none"
      comboboxProps={{
        width: dropdownWidth ?? "target",
        position: "bottom-start",
        offset: 6,
        shadow: "md",
        transitionProps: { transition: "pop-top-left", duration: 120 },
        // Body đang zoom 93%: render vào khung bù zoom để danh sách nằm ngay dưới ô, không che ô
        portalProps: { target: getZoomSafePortalTarget() },
      }}
      classNames={{
        input: inputClass,
        section: classes.section,
        dropdown: classes.dropdown,
        option: classes.option,
        empty: classes.empty,
      }}
      styles={{
        input: {
          height,
          minHeight: height,
          paddingRight: 28,
          paddingLeft: caption ? captionWidth : compact || plain ? 8 : 10,
        },
      }}
      renderOption={({ option, checked }) => {
        const item = byValue.get(option.value);
        return (
          <span className={classes.optionBody}>
            <span className={item?.hint ? classes.optionPrimary : classes.optionLabel}>{item?.primary ?? option.label}</span>
            {item?.hint && <span className={classes.optionHint}>{item.hint}</span>}
            {checked && <CheckIcon />}
          </span>
        );
      }}
    />
  );
};

export default FieldSelect;
